import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { resolveApiError, useTranslation } from '@eon/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  TextField,
  toast,
} from '@eon/ui';
import { hooks, type IdTag } from '@eon/api-client';

interface UpdateForm {
  value: string;
  name: string;
  description: string;
}

interface UpdateIdTagDialogProps {
  tag: IdTag | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UpdateIdTagDialog({ tag, open, onOpenChange }: UpdateIdTagDialogProps) {
  const { t } = useTranslation();
  const update = hooks.useUpdateIdTag();
  const { register, handleSubmit, reset, formState } = useForm<UpdateForm>();

  useEffect(() => {
    if (tag && open) {
      reset({
        value: tag.value,
        name: tag.name,
        description: tag.description ?? '',
      });
    }
  }, [tag, open, reset]);

  const onSubmit = handleSubmit(async (values) => {
    if (!tag) return;
    try {
      await update.mutateAsync({
        id: tag.id,
        value: values.value.trim(),
        name: values.name.trim(),
        description: values.description.trim() || undefined,
      });
      toast.success(t('idTag.updated'));
      onOpenChange(false);
    } catch (err) {
      toast.error(resolveApiError(err, t));
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('idTag.updateTitle')}</DialogTitle>
        </DialogHeader>
        {tag ? (
          <div className="bg-muted mb-4 rounded-md p-3 text-sm">
            <p>
              <span className="text-muted-foreground">{t('idTag.tag')}:</span>{' '}
              <span className="font-mono">{tag.value}</span>
            </p>
          </div>
        ) : null}
        <form onSubmit={onSubmit} className="space-y-4">
          <Field label={t('idTag.value')}>
            <TextField {...register('value', { required: true })} className="font-mono" />
          </Field>
          <Field label={t('idTag.name')}>
            <TextField {...register('name', { required: true })} />
          </Field>
          <Field label={t('idTag.description')}>
            <TextField {...register('description')} />
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('actions.cancel')}
            </Button>
            <Button type="submit" disabled={formState.isSubmitting || update.isPending}>
              {t('actions.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
